import { apiClient } from './apiClient';

export interface Attachment {
  id: string;
  taskId: string;
  fileName: string;
  fileSize: number;
  mimeType?: string | null;
  signatureCount: number;
  uploadedBy: string;
  uploadedByName?: string | null;
  uploadedAt: string;
}

export const attachmentService = {
  async list(taskId: string): Promise<Attachment[]> {
    const { data } = await apiClient.get<Attachment[]>(`/tasks/${taskId}/attachments`);
    return data;
  },

  async upload(taskId: string, file: File): Promise<Attachment> {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await apiClient.post<Attachment>(
      `/tasks/${taskId}/attachments`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  downloadUrl(attachmentId: string): string {
    const base = apiClient.defaults.baseURL ?? '';
    return `${base}/attachments/${attachmentId}/download`;
  },

  async download(attachmentId: string, fileName: string, useSigned: boolean = false): Promise<void> {
    // V1.0.42: useSigned=true → tải bản đã ký mới nhất (signed-latest endpoint)
    const endpoint = useSigned ? 'signed-latest' : 'download';
    const response = await apiClient.get(`/attachments/${attachmentId}/${endpoint}`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = useSigned
      ? fileName.replace(/(\.[^.]+)$/, '_signed$1')
      : fileName;
    a.click();
    URL.revokeObjectURL(url);
  },

  /**
   * V1.0.39: Load file qua API (có JWT) → trả về blob URL để dùng cho <img src> hoặc <iframe src>.
   * V1.0.42: useSigned=true → load bản đã ký mới nhất.
   * Caller chịu trách nhiệm gọi URL.revokeObjectURL khi không cần.
   */
  async previewBlob(attachmentId: string, mimeType?: string | null, useSigned: boolean = false): Promise<string> {
    const endpoint = useSigned ? 'signed-latest' : 'download';
    const response = await apiClient.get(`/attachments/${attachmentId}/${endpoint}`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: mimeType || (response.headers as any)['content-type'] || 'application/octet-stream',
    });
    return URL.createObjectURL(blob);
  },

  /**
   * V1.0.62: Trả về Blob trực tiếp (không URL.createObjectURL).
   * Dùng cho OfficeViewer cần đọc binary data của Word/Excel.
   */
  async fetchBlob(attachmentId: string, useSigned: boolean = false): Promise<Blob> {
    const endpoint = useSigned ? 'signed-latest' : 'download';
    const response = await apiClient.get(`/attachments/${attachmentId}/${endpoint}`, {
      responseType: 'blob',
    });
    return new Blob([response.data], {
      type: (response.headers as any)['content-type'] || 'application/octet-stream',
    });
  },

  async delete(attachmentId: string): Promise<void> {
    await apiClient.delete(`/attachments/${attachmentId}`);
  },
};

export const printService = {
  async openPrintWindow(taskId: string): Promise<void> {
    const response = await apiClient.get<string>(`/tasks/${taskId}/print/html`, {
      responseType: 'text',
    });
    const html = response.data;
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) {
      alert('Trình duyệt chặn popup. Vui lòng cho phép popup để in phiếu.');
      return;
    }
    w.document.write(html);
    w.document.close();
    // Đợi tải xong rồi in
    setTimeout(() => { w.focus(); w.print(); }, 500);
  },

  async downloadPdf(taskId: string): Promise<void> {
    // MVP: backend trả HTML file để browser save-as, user có thể chuyển sang PDF qua print dialog.
    const response = await apiClient.get(`/tasks/${taskId}/print/pdf`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `phieu-giao-viec-${taskId}.html`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
